<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>文件管理</h1>
        <p>全站上传文件登记与孤儿文件清理</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" plain :loading="scanning" @click="handleScan">
          <i class="el-icon-refresh" /> 扫描引用
        </el-button>
        <el-button type="danger" plain :loading="cleaning" :disabled="!stats.totalOrphan" @click="handleCleanOrphan">
          <i class="el-icon-delete" /> 一键清理孤儿
        </el-button>
      </div>
    </div>

    <!-- 顶部统计：一眼看清"系统里有多少图、多少在用、多少是孤儿、占多大空间" -->
    <div class="stat-row">
      <div class="stat-card">
        <div class="stat-num">{{ stats.total || 0 }}</div>
        <div class="stat-label">文件总数</div>
      </div>
      <div class="stat-card is-ok">
        <div class="stat-num is-ok">{{ stats.totalReferenced || 0 }}</div>
        <div class="stat-label">被引用</div>
      </div>
      <div class="stat-card is-warn">
        <div class="stat-num is-coral">{{ stats.totalOrphan || 0 }}</div>
        <div class="stat-label">孤儿文件</div>
        <div class="stat-sub">{{ formatBytes(stats.orphanSizeBytes) }} 可释放</div>
      </div>
      <div class="stat-card">
        <div class="stat-num">{{ formatBytes(stats.totalSizeBytes) }}</div>
        <div class="stat-label">总占用</div>
      </div>
      <div class="stat-card is-accent">
        <div class="stat-num is-rose">{{ storageLabel }}</div>
        <div class="stat-label">当前存储</div>
        <div class="stat-sub" v-if="storageLabel === '本地磁盘'">配好七牛云后在「系统配置」切换</div>
      </div>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-select v-model="filters.bizType" placeholder="全部业务类型" clearable class="mini-select" @change="fetchList">
        <el-option v-for="o in bizTypeOptions" :key="o.value" :label="o.label" :value="o.value" />
      </el-select>
      <el-select v-model="filters.uploaderType" placeholder="全部上传方" clearable class="mini-select" @change="fetchList">
        <el-option label="用户" value="USER" />
        <el-option label="管理员" value="ADMIN" />
        <el-option label="系统" value="SYSTEM" />
      </el-select>
      <el-select v-model="filters.storageType" placeholder="全部存储位置" clearable class="mini-select" @change="fetchList">
        <el-option label="本地磁盘" value="LOCAL" />
        <el-option label="七牛云" value="QINIU" />
      </el-select>
      <el-input v-model="filters.keyword" placeholder="文件名 / key" clearable prefix-icon="el-icon-search" class="search-input" @keyup.enter.native="fetchList" @clear="fetchList" />
      <el-checkbox v-model="filters.orphanOnly" @change="fetchList">只看孤儿</el-checkbox>
      <div class="filter-tail">共 <b>{{ total }}</b> 个</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table :data="list" v-loading="loading" style="width: 100%">
        <el-table-column label="预览" width="90">
          <template slot-scope="{ row }">
            <el-image
              v-if="isImage(row)"
              :src="row.url"
              :preview-src-list="[row.url]"
              fit="cover"
              class="thumb"
            />
            <div v-else class="thumb thumb--video">
              <i class="el-icon-video-camera" />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="文件" min-width="230">
          <template slot-scope="{ row }">
            <div class="cell-main">{{ row.originalName || '未命名' }}</div>
            <div class="cell-sub cell-mono">{{ row.fileKey }}</div>
          </template>
        </el-table-column>
        <el-table-column label="存储" width="100">
          <template slot-scope="{ row }">
            <span class="tag" :class="row.storageType === 'QINIU' ? 'is-gold' : ''">
              {{ row.storageType === 'QINIU' ? '七牛云' : '本地' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="业务" width="100">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ bizLabel(row.bizType) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="上传方" width="90">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ uploaderLabel(row.uploaderType) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="大小" width="100">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ formatBytes(row.sizeBytes) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="引用" width="100">
          <template slot-scope="{ row }">
            <span v-if="row.refCount > 0" class="tag is-plain">{{ row.refCount }} 处</span>
            <span v-else class="tag is-rose">孤儿</span>
          </template>
        </el-table-column>
        <el-table-column label="上传时间" width="170">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ row.createdAt || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button type="button" class="act act-danger" @click="handleDelete(row)">删除</button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :current-page.sync="page.current"
          :page-size.sync="page.size"
          :page-sizes="[20, 50, 100]"
          @current-change="fetchList"
          @size-change="fetchList"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getFileList, scanFileReferences, deleteFile, cleanOrphanFiles } from '@/api/file'

export default {
  name: 'File',
  data() {
    return {
      loading: false,
      scanning: false,
      cleaning: false,
      list: [],
      total: 0,
      stats: {},
      page: { current: 1, size: 20 },
      filters: { bizType: '', uploaderType: '', storageType: '', orphanOnly: false, keyword: '' },
      bizTypeOptions: [
        { value: 'AVATAR', label: '头像' },
        { value: 'LIFE_PHOTO', label: '生活照' },
        { value: 'MOMENT_IMAGE', label: '动态图片' },
        { value: 'MOMENT_VIDEO', label: '动态视频' },
        { value: 'VERIFY', label: '认证图' },
        { value: 'OTHER', label: '其他' }
      ]
    }
  },
  computed: {
    storageLabel() {
      // 从配置项 file.storage.type 读：本页不直接拿配置接口，用列表里出现过的存储类型推断
      // 更稳妥的是单独请求，但为减少接口，列表里若全是 LOCAL 视作本地，有 QINIU 视作含七牛
      const hasQiniu = this.list.some(r => r.storageType === 'QINIU')
      return hasQiniu ? '含七牛云' : '本地磁盘'
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    fetchList() {
      this.loading = true
      const params = {
        page: this.page.current,
        size: this.page.size,
        bizType: this.filters.bizType || undefined,
        uploaderType: this.filters.uploaderType || undefined,
        storageType: this.filters.storageType || undefined,
        orphanOnly: this.filters.orphanOnly ? true : undefined,
        keyword: this.filters.keyword || undefined
      }
      getFileList(params).then(res => {
        const d = (res.data && res.data) || {}
        this.list = d.rows || []
        this.total = d.total || 0
        this.stats = {
          total: d.total,
          totalReferenced: d.totalReferenced,
          totalOrphan: d.totalOrphan,
          totalSizeBytes: d.totalSizeBytes,
          orphanSizeBytes: d.orphanSizeBytes
        }
      }).finally(() => {
        this.loading = false
      })
    },
    handleScan() {
      this.scanning = true
      scanFileReferences().then(res => {
        const d = res.data || {}
        this.$alert(
          `扫描完成：共 ${d.totalRecords} 个文件，被引用 ${d.referencedRecords} 个，孤儿 ${d.orphanRecords} 个（可释放 ${this.formatBytes(d.orphanSizeBytes)}）。`,
          '引用扫描结果',
          { type: 'success', confirmButtonText: '知道了' }
        )
        this.fetchList()
      }).finally(() => {
        this.scanning = false
      })
    },
    handleCleanOrphan() {
      const n = this.stats.totalOrphan || 0
      if (!n) {
        this.$message.info('没有孤儿文件')
        return
      }
      this.$confirm(
        `将清理 ${n} 个未被任何地方引用的孤儿文件，释放约 ${this.formatBytes(this.stats.orphanSizeBytes)} 空间。此操作不可撤销，确认吗？`,
        '一键清理孤儿',
        { type: 'warning', confirmButtonText: '清理', cancelButtonText: '取消' }
      ).then(() => {
        this.cleaning = true
        cleanOrphanFiles().then(res => {
          const d = res.data || {}
          this.$message.success(`已清理 ${d.cleanedCount} 个，释放 ${this.formatBytes(d.releasedBytes)}` + (d.failedCount ? `，失败 ${d.failedCount} 个` : ''))
          this.fetchList()
        }).finally(() => {
          this.cleaning = false
        })
      }).catch(() => {})
    },
    handleDelete(row) {
      const orphan = row.refCount === 0
      const tip = orphan
        ? `该文件无引用，删除后物理文件也会清理。确认删除？`
        : `该文件被 ${row.refCount} 处引用，删除后相关位置将显示裂图。建议「扫描引用」确认后，勾选强制删除。`
      this.$confirm(tip, '删除文件', {
        type: 'warning',
        confirmButtonText: orphan ? '删除' : '强制删除',
        cancelButtonText: '取消'
      }).then(() => {
        deleteFile(row.id, !orphan).then(res => {
          const d = res.data || {}
          this.$message.success(`已删除，释放 ${this.formatBytes(d.releasedBytes)}`)
          this.fetchList()
        })
      }).catch(() => {})
    },
    isImage(row) {
      if (row.mimeType && row.mimeType.startsWith('image/')) return true
      if (row.fileExt) {
        return ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'].includes(row.fileExt.toLowerCase())
      }
      return row.bizType !== 'MOMENT_VIDEO'
    },
    bizLabel(t) {
      const m = { AVATAR: '头像', LIFE_PHOTO: '生活照', MOMENT_IMAGE: '动态图片', MOMENT_VIDEO: '动态视频', VERIFY: '认证图', OTHER: '其他' }
      return m[t] || t
    },
    uploaderLabel(t) {
      const m = { USER: '用户', ADMIN: '管理员', SYSTEM: '系统' }
      return m[t] || t
    },
    formatBytes(b) {
      if (!b || b <= 0) return '0 B'
      if (b < 1024) return b + ' B'
      if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB'
      if (b < 1024 * 1024 * 1024) return (b / 1024 / 1024).toFixed(2) + ' MB'
      return (b / 1024 / 1024 / 1024).toFixed(2) + ' GB'
    }
  }
}
</script>

<style scoped>
.mini-select {
  width: 150px;
}

.file-table .thumb,
.thumb {
  width: 60px;
  height: 60px;
  border-radius: var(--r-sm);
  overflow: hidden;
  background: var(--paper);
  cursor: pointer;
}

.thumb--video {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-350);
  font-size: 22px;
  cursor: default;
}
</style>
